import type { ApiResponse, ContactDetailsDto } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

/** Shared key so the read hook and the update mutation target the same cache entry. */
export const contactDetailsQueryKey = (creatorIdentityId: string | null) =>
  ['contact-details', creatorIdentityId] as const;

/** Empty override used as the fallback when the endpoint returns no row yet. */
export const EMPTY_DETAILS: ContactDetailsDto = { email: null, phone: null };

const fetchContactDetails = async (creatorIdentityId: string): Promise<ContactDetailsDto> => {
  const { data } = await api.get<ApiResponse<ContactDetailsDto>>(
    `/dashboard/contacts/${creatorIdentityId}/details`
  );

  return data.data ?? EMPTY_DETAILS;
};

/**
 * Lazy contact-override slice for the unified contact card, keyed by
 * `creatorIdentityId`. Only fires when a contact is resolved. Returns the brand's
 * editable email/phone override (both `null` until the brand sets them).
 */
export const useContactDetailsQuery = (creatorIdentityId: string | null) => {
  return useQuery({
    queryKey: contactDetailsQueryKey(creatorIdentityId),
    queryFn: () => fetchContactDetails(creatorIdentityId!),
    enabled: !!creatorIdentityId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
